import type { SectionPair } from '../types';
import {
    NodeBaseModel,
    ComparisonNodeBaseModel,
    BasicComparisonNodeModel,
    LayersComparisonNodeModel,
    SectionsComparisonNodeModel
} from '../models';
import { InstanceUtils, ContractComparisonPanels } from '../misc';

interface CreateComparisonNodeParameters {
    nodeId: string;
    nodeName: string;
    newNode?: NodeBaseModel;
    previousNode?: NodeBaseModel;
    layerId?: string;
    getSectionPairs?: () => readonly SectionPair[];
}

export default class ComparisonNodeFactory {
    static createComparisonNode(parameters: CreateComparisonNodeParameters): ComparisonNodeBaseModel | null {
        const { nodeId, nodeName, newNode, previousNode, layerId, getSectionPairs } = parameters;

        [newNode, previousNode].forEach(node => {
            if (InstanceUtils.isLayersWithSectionsNode(node) && !node.layers.some(layer => layer.id === layerId)) {
                throw new Error('Section comparison requires a valid selected layer');
            }
        });

        const isNewNodeBasic = InstanceUtils.isBasicNode(newNode);
        const isPreviousNodeBasic = InstanceUtils.isBasicNode(previousNode);

        if (isNewNodeBasic || isPreviousNodeBasic) {
            return new BasicComparisonNodeModel(
                nodeId,
                nodeName,
                ContractComparisonPanels[nodeId as keyof typeof ContractComparisonPanels],
                isNewNodeBasic ? newNode : undefined,
                isPreviousNodeBasic ? previousNode : undefined
            );
        }

        const isNewNodeSections = InstanceUtils.isSectionedNode(newNode);
        const isPreviousNodeSections = InstanceUtils.isSectionedNode(previousNode);

        if (isNewNodeSections || isPreviousNodeSections) {
            if (!getSectionPairs) {
                throw new Error('Section comparison requires a section pair provider');
            }

            return new SectionsComparisonNodeModel(
                nodeId,
                nodeName,
                ContractComparisonPanels[nodeId as keyof typeof ContractComparisonPanels],
                isNewNodeSections ? newNode : undefined,
                isPreviousNodeSections ? previousNode : undefined,
                getSectionPairs,
                layerId
            );
        }

        if (layerId) {
            const isNewNodeLayers = InstanceUtils.isLayersNode(newNode);
            const isPreviousNodeLayers = InstanceUtils.isLayersNode(previousNode);

            return new LayersComparisonNodeModel(
                nodeId,
                nodeName,
                layerId,
                ContractComparisonPanels[nodeId as keyof typeof ContractComparisonPanels],
                isNewNodeLayers ? newNode : undefined,
                isPreviousNodeLayers ? previousNode : undefined
            );
        }

        return null;
    }
}
